'use client';

import { PLAYER_CONFIG } from '@/features/video/constants/player-constants';
import type {
  ShakaModule,
  ShakaPlayerInstance,
} from '@/features/video/components/shaka-player/types';
import { captureError } from '@/shared/lib/errors/capture';
import { useCallback, useEffect, useRef, useState } from 'react';

const { HOVER_PREVIEW } = PLAYER_CONFIG;

interface UsePreviewShakaReturn {
  play: () => void;
  stop: () => void;
  isReady: boolean;
}

export function usePreviewShaka(
  videoRef: React.RefObject<HTMLVideoElement | null>,
  hlsUrl: string | null
): UsePreviewShakaReturn {
  const [isReady, setIsReady] = useState(false);
  const shakaRef = useRef<ShakaPlayerInstance>(null);
  const shakaModuleRef = useRef<ShakaModule>(null);

  const stop = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    if (shakaRef.current) {
      shakaRef.current.destroy().catch(() => {});
      shakaRef.current = null;
    }

    setIsReady(false);
  }, [videoRef]);

  const play = useCallback(() => {
    if (!hlsUrl || !videoRef.current) return;

    const video = videoRef.current;

    const init = async () => {
      try {
        // Dynamic import — cached by bundler after first call
        if (!shakaModuleRef.current) {
          const shaka = (await import('shaka-player')) as ShakaModule;
          shaka.polyfill.installAll();
          if (!shaka.Player.isBrowserSupported()) return;
          shakaModuleRef.current = shaka;
        }

        // Destroy any leftover instance
        if (shakaRef.current) {
          await shakaRef.current.destroy();
          shakaRef.current = null;
        }

        const shaka = shakaModuleRef.current;
        const player = new shaka.Player();
        await player.attach(video);
        shakaRef.current = player;

        player.configure({
          streaming: {
            bufferingGoal: HOVER_PREVIEW.BUFFERING_GOAL,
            rebufferingGoal: HOVER_PREVIEW.REBUFFERING_GOAL,
            bufferBehind: HOVER_PREVIEW.BUFFER_BEHIND,
            lowLatencyMode: false,
          },
          abr: {
            enabled: false,
          },
          restrictions: {
            maxHeight: HOVER_PREVIEW.MAX_HEIGHT,
          },
        });

        await player.load(hlsUrl);

        // Lock to lowest variant after manifest is loaded
        const tracks = player.getVariantTracks();
        if (tracks.length > 0) {
          const lowest = tracks.reduce(
            (min: { bandwidth: number }, t: { bandwidth: number }) =>
              t.bandwidth < min.bandwidth ? t : min,
            tracks[0]
          );
          player.selectVariantTrack(lowest, /* clearBuffer= */ true);
        }

        video.currentTime = 0;
        await video.play();
      } catch (err: unknown) {
        // AbortError is expected when user unhovers before play() resolves
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Shaka 7000 (LOAD_INTERRUPTED) is the Shaka-level equivalent: the
        // preview load was interrupted by unhover. Routine, not actionable.
        if (
          err !== null &&
          typeof err === 'object' &&
          'code' in err &&
          (err as { code: unknown }).code === 7000
        ) {
          return;
        }
        captureError(err, {
          component: 'RailCard',
          action: 'hls-preview',
          severity: 'info',
          skipPostHog: true,
        });
      }
    };

    init();
  }, [hlsUrl, videoRef]);

  // Loop at LOOP_DURATION
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !hlsUrl) return;

    const onTimeUpdate = () => {
      if (video.currentTime >= HOVER_PREVIEW.LOOP_DURATION) {
        video.currentTime = 0;
      }
    };

    const onPlaying = () => setIsReady(true);

    video.addEventListener('timeupdate', onTimeUpdate);
    video.addEventListener('playing', onPlaying);

    return () => {
      video.removeEventListener('timeupdate', onTimeUpdate);
      video.removeEventListener('playing', onPlaying);
    };
  }, [videoRef, hlsUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (shakaRef.current) {
        shakaRef.current.destroy().catch(() => {});
        shakaRef.current = null;
      }
    };
  }, []);

  return { play, stop, isReady };
}
